/**
 * Dealer Dashboard — Results-first restructure (iter139 F, Feb 2026)
 *
 * The dealer's real question when they open this page is:
 *   "How many people came into my store because of this?"
 *
 * So the top of the page now leads with the OUTCOME:
 *   • Showroom Visits This Month (redeemed test-drives) — headline, largest
 *   • Verified Leads — the credibility number
 *   • Show-up rate (redeemed / signed up)
 *
 * Total-leads and lead-sources still exist below, demoted to supporting
 * context. No metric calculations changed — same endpoints, reprioritized.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Users, Calendar, ShieldCheck, Store,
  AlertCircle, RefreshCw, MapPin, Inbox, TrendingUp,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import { TimeRangeSelector } from "../components/dashboard";
import { startOfCurrentMonthMs } from "../utils/dateRanges";
import { SOURCE_ORDER, bucketLeadsBySource } from "../utils/leadSources";

// Hero card — outcome-first, largest visual weight. This is what the
// dealer's eye lands on first.
const HeroCard = ({ label, value, sublabel, icon: Icon, accent, testId }) => (
  <div
    className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4 shadow-sm"
    style={{ borderTop: `4px solid ${accent}`, minHeight: 128 }}
    data-testid={testId}
  >
    <div
      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ background: `${accent}18` }}
    >
      <Icon size={22} style={{ color: accent }} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider m-0">{label}</p>
      <p
        className="font-bold text-gray-900 leading-none tabular-nums m-0 mt-1"
        style={{ fontSize: 40, letterSpacing: '-0.02em' }}
      >
        {value}
      </p>
      {sublabel && (
        <p className="text-[12.5px] text-gray-600 m-0 mt-1.5">{sublabel}</p>
      )}
    </div>
  </div>
);

// Small supporting-context card — demoted from the original prominent
// row. Same StatCardCompact shape as before.
const StatCardCompact = ({ accent, label, value, icon: Icon, testId }) => (
  <div
    className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center gap-3"
    style={{ borderLeft: `4px solid ${accent}`, minHeight: 78, maxHeight: 110 }}
    data-testid={testId}
  >
    <div
      className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
      style={{ background: `${accent}15` }}
    >
      <Icon size={18} style={{ color: accent }} />
    </div>
    <div className="min-w-0">
      <p className="text-xl font-bold text-gray-900 leading-tight tabular-nums">{value}</p>
      <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  </div>
);

const SourcesTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
      <p className="font-semibold">{p.payload.name}</p>
      <p className="text-gray-300">{p.value.toLocaleString()} leads</p>
    </div>
  );
};

const DealerDashboardPage = () => {
  const { apiFetch, user } = useAuth();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashData, setDashData] = useState(null);
  const [allLeads, setAllLeads] = useState([]);
  const [unlockedLeads, setUnlockedLeads] = useState([]);
  const [visitsSummary, setVisitsSummary] = useState({ signed_up: 0, redeemed: 0 });
  const abortRef = useRef(null);

  const fetchData = useCallback(async (selectedDays) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    try {
      setLoading(true);
      setError(null);
      const [dashRes, leadsRes, unlockedRes, redemptionRes] = await Promise.all([
        apiFetch(`/dashboard/overview?days=${selectedDays}`, { signal: abortRef.current.signal }).catch(() => ({ data: {} })),
        apiFetch(`/leads?limit=500`, { signal: abortRef.current.signal }).catch(() => ({ data: [] })),
        apiFetch(`/unlock/leads`, { signal: abortRef.current.signal }).catch(() => ({ data: { leads: [] } })),
        // iter139 F — pull the Showroom Visits funnel so the hero can lead
        // with the redeemed count. Same endpoint the Showroom Visits page
        // uses; no new metrics invented.
        apiFetch(`/dealer/redemption-log`, { signal: abortRef.current.signal }).catch(() => ({ data: { summary: {} } })),
      ]);
      setDashData(dashRes.data || {});
      setAllLeads(Array.isArray(leadsRes.data) ? leadsRes.data : []);
      setUnlockedLeads(unlockedRes.data?.leads || []);
      const s = redemptionRes?.data?.summary || {};
      setVisitsSummary({
        signed_up: s.signed_up || 0,
        redeemed: s.redeemed || 0,
      });
    } catch (err) {
      if (err.name === "AbortError" || err.name === "CanceledError") return;
      setError(err.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    fetchData(days);
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [days, fetchData]);

  if (error && !loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] bg-red-50 rounded-xl p-6" data-testid="dealer-dashboard">
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-red-600 font-medium">{error}</p>
        <button onClick={() => fetchData(days)} className="btn-secondary mt-3">
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }

  // ── Derived metrics (same computations as before, reprioritized) ──
  const combined = [...allLeads, ...unlockedLeads];
  const uniqueById = Array.from(new Map(combined.map(l => [l.id, l])).values());
  const cutoffMs = Date.now() - days * 24 * 60 * 60 * 1000;
  const filteredByDays = uniqueById.filter(l => {
    const t = l.created_at ? new Date(l.created_at).getTime() : NaN;
    return Number.isFinite(t) ? t >= cutoffMs : true;
  });
  const totalLeads = filteredByDays.length;
  const tz = user?.business_timezone || "America/New_York";
  const monthStartMs = startOfCurrentMonthMs(tz);
  const leadsThisMonth = uniqueById.filter(l => {
    const t = l.created_at ? new Date(l.created_at).getTime() : NaN;
    return Number.isFinite(t) && t >= monthStartMs;
  }).length;
  const verifiedLeads = filteredByDays.filter(l => l.is_verified || l.verified_mobile).length;

  // ── Showroom Visits hero metrics (from redemption-log endpoint) ──
  // redeemed = customers who signed up AND walked in AND got the reward.
  // showUpRate = redeemed / signed_up (booked → showed conversion).
  const showroomVisits = visitsSummary.redeemed || 0;
  const showUpRate = visitsSummary.signed_up > 0
    ? Math.round((visitsSummary.redeemed / visitsSummary.signed_up) * 100)
    : 0;

  const sourcesData = bucketLeadsBySource(uniqueById).filter(s => s.value > 0);
  const hasSources = sourcesData.length > 0;
  const chartData = hasSources ? sourcesData : SOURCE_ORDER.map(s => ({ name: s.key, value: 0, color: s.color }));

  const topZips = (dashData?.top_zip_codes || []).slice(0, 5);

  return (
    <div data-testid="dealer-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-[20px] font-bold text-gray-900 m-0">Dashboard</h1>
          <p className="text-[13px] text-gray-500 mt-0.5 m-0">Showroom visits, verified leads & where demand comes from</p>
        </div>
        <TimeRangeSelector value={days} onChange={setDays} disabled={loading} />
      </div>

      {/* ─── HERO ROW — Results First ────────────────────────────────
          Showroom visits is the headline number. Verified leads and
          show-up rate flank it. This block gets the largest visual
          weight so a 5-second glance answers "how many walked in?" */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5" data-testid="hero-results-row">
        <HeroCard
          label="Showroom Visits · This Month"
          value={loading ? "—" : showroomVisits.toLocaleString()}
          sublabel={
            loading
              ? "Loading foot traffic…"
              : `${visitsSummary.signed_up.toLocaleString()} signed up through the popup`
          }
          icon={Store}
          accent="#DC2626"
          testId="hero-showroom-visits"
        />
        <HeroCard
          label="Verified Leads"
          value={loading ? "—" : verifiedLeads.toLocaleString()}
          sublabel={loading ? " " : `Phone-verified in the last ${days} days`}
          icon={ShieldCheck}
          accent="#10B981"
          testId="hero-verified-leads"
        />
        <HeroCard
          label="Show-up Rate"
          value={loading ? "—" : `${showUpRate}%`}
          sublabel={
            loading
              ? " "
              : visitsSummary.signed_up > 0
                ? `${visitsSummary.redeemed} of ${visitsSummary.signed_up} booked customers walked in`
                : "No test-drive bookings yet"
          }
          icon={TrendingUp}
          accent="#005CFF"
          testId="hero-show-up-rate"
        />
      </div>

      {/* ─── SUPPORTING CONTEXT — Total Leads / Leads This Month ────
          Demoted below the hero. Same numbers as before, smaller. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-testid="kpi-grid">
        <StatCardCompact
          accent="#64748B"
          label="Total Leads"
          value={loading ? "—" : totalLeads.toLocaleString()}
          icon={Users}
          testId="kpi-total-leads"
        />
        <StatCardCompact
          accent="#64748B"
          label="Leads This Month"
          value={loading ? "—" : leadsThisMonth.toLocaleString()}
          icon={Calendar}
          testId="kpi-leads-this-month"
        />
      </div>

      {/* ─── ANALYTICS — Lead Sources + Top ZIPs ──────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">
        <div className="lg:col-span-3 bg-white rounded-lg border border-gray-200 p-4" data-testid="lead-sources-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-[13px] font-semibold text-gray-900 m-0">Lead Sources</h3>
              <p className="text-[11px] text-gray-500 mt-0.5 m-0">Where your leads come from</p>
            </div>
            <span className="text-[10px] font-medium text-gray-400 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded">
              Last {days} days
            </span>
          </div>
          {!hasSources && !loading ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Inbox className="w-9 h-9 text-gray-300 mb-2" />
              <p className="text-[13px] text-gray-500 font-medium">No lead source data yet</p>
              <p className="text-[11px] text-gray-400 mt-0.5">Leads will appear here once captured through the CTA.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid horizontal={false} stroke="#F3F4F6" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#4B5563" }}
                  axisLine={false}
                  tickLine={false}
                  width={80}
                />
                <Tooltip content={<SourcesTooltip />} cursor={{ fill: "#F9FAFB" }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={16}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-4" data-testid="top-zips-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-[13px] font-semibold text-gray-900 m-0 flex items-center gap-1.5">
                <MapPin size={13} className="text-purple-600" /> Top ZIP Codes
              </h3>
              <p className="text-[11px] text-gray-500 mt-0.5 m-0">Demand volume by area</p>
            </div>
          </div>
          {topZips.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <MapPin className="w-9 h-9 text-gray-300 mb-2" />
              <p className="text-[13px] text-gray-500 font-medium">No geographic data yet</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {topZips.map((z, idx) => (
                <li key={z.zip_code} className="flex items-center gap-3 py-2.5" data-testid={`top-zip-${idx}`}>
                  <span className={`flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold ${idx < 3 ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    {/* iter83 — strip stray whitespace from zip_code so
                        upstream values like "971 04" render as "97104". */}
                    <p className="text-[13px] font-semibold text-gray-900 m-0">{String(z.zip_code || '').replace(/\s+/g, '')}</p>
                    {z.city && <p className="text-[11px] text-gray-500 m-0 truncate">{z.city}{z.state ? `, ${z.state}` : ''}</p>}
                  </div>
                  <span className="text-[13px] font-semibold text-gray-900 tabular-nums">{(z.visitor_count || 0).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealerDashboardPage;
